/**
 * Frontend port of the backend's sprayWeatherService categorisation logic
 * (backend/src/services/sprayWeatherService.js). Boundaries are inclusive
 * to the lower bucket so a reading of exactly coldMax stays Cold and a
 * reading of exactly optimalMax stays Optimal.
 *
 * Used by Field Mode sign-off to compute the system-reported category from
 * cached raw weather, and to compare against the operator's selection so we
 * can show a Verified vs Manual Override indicator.
 *
 * Track: automatic_weather_tracking_20260428 / Phase 4 / Task 4.
 */

export type TemperatureCategory = 'Cold' | 'Optimal' | 'Hot';
export type WindCategory = 'Calm' | 'Breeze' | 'Strong';
export type PrecipitationCategory = 'None' | 'Light' | 'Heavy';

export interface WeatherThresholds {
  temperature: { coldMax: number; optimalMax: number };
  wind: { calmMax: number; breezeMax: number };
  precipitation: { noneMax: number; lightMax: number };
}

export const DEFAULT_THRESHOLDS: WeatherThresholds = {
  temperature: { coldMax: 10, optimalMax: 28 },
  wind: { calmMax: 2, breezeMax: 15 },
  precipitation: { noneMax: 0, lightMax: 2 },
};

export const categorizeTemperature = (
  tempC: number,
  t: WeatherThresholds['temperature']
): TemperatureCategory => {
  if (tempC <= t.coldMax) return 'Cold';
  if (tempC <= t.optimalMax) return 'Optimal';
  return 'Hot';
};

export const categorizeWind = (
  kph: number,
  w: WeatherThresholds['wind']
): WindCategory => {
  if (kph <= w.calmMax) return 'Calm';
  if (kph <= w.breezeMax) return 'Breeze';
  return 'Strong';
};

export const categorizePrecipitation = (
  mm: number,
  p: WeatherThresholds['precipitation']
): PrecipitationCategory => {
  if (mm <= p.noneMax) return 'None';
  if (mm <= p.lightMax) return 'Light';
  return 'Heavy';
};
